import {
  animate,
  style,
  transition,
  trigger,
} from '@angular/animations';
import {
  ChangeDetectionStrategy,
  Component,
  output,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import type { ModelMetadataEntryMode, ModelOnboardingSelection } from '../model.models';

interface MetadataPathOption {
  mode: ModelMetadataEntryMode;
  title: string;
  icon: 'pencil' | 'upload';
  description: string;
}

interface ModelResourceServerOption {
  id: 'file-server';
  title: string;
  description: string;
}

@Component({
  selector: 'lib-model-metadata-select',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './model-metadata-select.component.html',
  styleUrl: './model-metadata-select.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('modelMetaReveal', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(1.25rem)' }),
        animate(
          '420ms cubic-bezier(0.22, 1, 0.36, 1)',
          style({ opacity: 1, transform: 'translateY(0)' }),
        ),
      ]),
    ]),
  ],
})
export class ModelMetadataSelectComponent {
  readonly selectionComplete = output<ModelOnboardingSelection>();

  readonly servers: ModelResourceServerOption[] = [
    {
      id: 'file-server',
      title: 'File Server',
      description:
        'Host models on the Files Connect server. Best for CSV, documents, and direct multipart file uploads.',
    },
  ];

  readonly metadataPaths: MetadataPathOption[] = [
    {
      mode: 'manual',
      title: 'Enter Manually',
      icon: 'pencil',
      description: 'Fill in the fields manually',
    },
    {
      mode: 'json-upload',
      title: 'Upload Metadata',
      icon: 'upload',
      description: 'Upload a pre-configured JSON or CSV file directly',
    },
  ];

  readonly selectedServer = signal<'file-server'>('file-server');
  readonly metadataMode = signal<ModelMetadataEntryMode | null>(null);

  selectServer(id: 'file-server'): void {
    this.selectedServer.set(id);
  }

  selectMetadataMode(mode: ModelMetadataEntryMode): void {
    this.metadataMode.set(mode);
  }

  canContinue(): boolean {
    return this.metadataMode() !== null;
  }

  onContinue(): void {
    const mode = this.metadataMode();
    if (!mode) return;
    this.selectionComplete.emit({ metadataMode: mode });
  }
}
